import { dataManager } from '../APIEntity';
import { ActionInfo } from '../models/ActionInfo';
import { normalizeEmail } from '../utils/utils';
import { sendReply } from './notifications';
import {
  Conversation,
  ConversationParticipant,
  createProjectConversation,
} from './conversation';
import { FlowpadDiagnosis } from './flowpad-diagnosis';

/**
 * Unified send payload. ``text`` may be empty when ``assetReferences`` or
 * ``files`` carry the meaning (e.g. share-a-doc with no commentary).
 */
export interface ConversationSendPayload {
  text: string;
  files?: File[];
  /** Serialized TypeIds (e.g. ``"markdown-<uuid>"``) for TYPE_ID attachments. */
  assetReferences?: string[];
  /** Serialized TypeIds to publish as the FlowMessage's *shared* context. On
   *  the existing-conversation path the local backend (handle_add_message)
   *  also merges these onto the parent Conversation and links them back —
   *  parity with the new-conversation path, without minting a new invite. */
  sharedContextEntities?: string[];
  /** Body-bundle transfer policy + per-share opt-ins. Defaults to copy. */
  shareConfig?: {
    transferMode?: 'copy' | 'git' | 'both';
    /** When true, the receiver mints a FAVORITE bookmark pointing at the shared
     *  asset when it installs. Default off. */
    createBookmark?: boolean;
    /** Send files git excludes too — set only after the sender saw them listed. */
    includeGitignored?: boolean;
  };
}

/** Send into an already-existing conversation. Thin wrap over ``sendReply`` —
 *  NEVER calls ``conv.share()``, so it can never mint a new invitation. */
export async function sendToExistingConversation(
  conversationId: string,
  payload: ConversationSendPayload,
): Promise<void> {
  const hasAssetRefs = !!payload.assetReferences?.length;
  const hasSharedCtx = !!payload.sharedContextEntities?.length;
  const extras = {
    ...(hasAssetRefs ? { assetReferences: payload.assetReferences } : {}),
    ...(hasSharedCtx ? { sharedContextEntities: payload.sharedContextEntities } : {}),
    ...(payload.shareConfig ? { shareConfig: payload.shareConfig } : {}),
  };
  await sendReply(
    { conversationId },
    payload.text,
    payload.files,
    hasAssetRefs || hasSharedCtx || !!payload.shareConfig ? extras : undefined,
  );
}

/** An attachment the sender cannot actually ship — the packer would write nothing for it, so the
 *  recipient would get a bundle without it and nobody would be told. */
export interface UnshippableReference {
  /** The serialized TypeId, e.g. ``"claude_session-<uuid>"``. */
  type_id: string;
  /** Why it would arrive empty, in a sentence. */
  reason: string;
}

/** An attachment whose copy would carry files git excludes — often private (a data source's local
 *  copy, an env file). The sender is shown them and decides; nothing is dropped silently. */
export interface GitignoredReference {
  /** The serialized TypeId. */
  type_id: string;
  /** The excluded files and folders, relative to the asset; `.` when the asset itself is excluded. */
  paths: string[];
}

export interface ShareAttachmentCheck {
  unshippable: UnshippableReference[];
  gitignored: GitignoredReference[];
}

/**
 * The read-only preflight of a share, asked BEFORE anything is created: what would arrive empty, and what
 * would carry files git excludes. A git-mode share sends a reference, so it is never asked about those.
 *
 * Sharing into a NEW conversation creates it and invites the recipient first and sends second, so a
 * refusal at send time leaves them holding an invitation to an empty conversation. Asking here costs
 * one read-only call and lets the caller offer "send without it" (or "send anyway") instead.
 */
export async function checkShareAttachments(
  assetReferences: readonly string[],
  options: { transferMode?: 'copy' | 'git' | 'both' } = {},
): Promise<ShareAttachmentCheck> {
  const refs = [...new Set(assetReferences)];
  if (refs.length === 0) return { unshippable: [], gitignored: [] };
  const info = new ActionInfo('check-attachments', null, null, 'POST');
  info.bodyParameters = { asset_references: refs, share_config: { transfer_mode: options.transferMode ?? 'copy' } };
  const result = await dataManager.callAction<
    { asset_references: string[]; share_config: { transfer_mode: string } },
    Partial<ShareAttachmentCheck>
  >(info);
  return { unshippable: result?.unshippable ?? [], gitignored: result?.gitignored ?? [] };
}

/** The same payload minus these references — from the attachments AND from the shared context, so
 *  the conversation is never left pointing at something that was not sent. */
export function withoutReferences(
  payload: ConversationSendPayload,
  drop: ReadonlySet<string>,
): ConversationSendPayload {
  return {
    ...payload,
    ...(payload.assetReferences ? { assetReferences: payload.assetReferences.filter((r) => !drop.has(r)) } : {}),
    ...(payload.sharedContextEntities
      ? { sharedContextEntities: payload.sharedContextEntities.filter((r) => !drop.has(r)) }
      : {}),
  };
}

/** Whether a send still carries anything the backend will accept (text, files or an attachment —
 *  shared context alone is not a message). */
export function hasSomethingToSend(payload: ConversationSendPayload): boolean {
  return !!(payload.text.trim() || payload.files?.length || payload.assetReferences?.length);
}

/**
 * Forward a diagnosis into a conversation by **attaching the FlowpadDiagnosis
 * entity** (a TYPE_ID attachment) — not by pasting its full text. It renders as
 * an entity chip; clicking the chip opens the diagnosis viewer (`buildDockPointer`
 * → `DockPointer.forDiagnosis`). A short `Diagnosis: <title>` caption rides along
 * so the message is never blank before the chip materializes (the chip needs the
 * entity resolved locally; the caption is plain text and shows immediately).
 *
 * No explicit un-hide: appending this message already auto-revives a dismissed
 * conversation in the Recent strip (the strip hides a row only UNTIL a newer
 * FlowMessage lands). A manual `conv.save()` would be a full conversation update,
 * which the hub forbids for `member`-role participants — a needless 401.
 *
 * Shared by every "Forward" surface — the Home-Feed card, the diagnosis viewer/
 * popup, the live diagnose modal, and the System Diagnoses table. Callers do
 * their own follow-up (e.g. the modal closes).
 */
export async function forwardDiagnosis(
  conversationId: string,
  diagnosisId: string,
): Promise<void> {
  const diag = await FlowpadDiagnosis.getById<FlowpadDiagnosis>(diagnosisId);
  const title = diag?.title || diag?.name || 'Diagnosis';
  await sendToExistingConversation(conversationId, {
    text: `Diagnosis: ${title}`,
    assetReferences: [`${FlowpadDiagnosis.type}-${diagnosisId}`],
  });
}

/**
 * Email a diagnosis to the Flowpad team. Calls the backend ``report`` action
 * (``POST /api/v1/graph/report``), which gathers the diagnosis's interesting
 * parts (what happened, the user's own words, who/when/which OS) and relays them
 * to the hub — the hub holds the SendGrid key and sends to diagnosis@langware.ai.
 * Used by the Home-Feed card's and the diagnose modal's "Report issue" button.
 */
export async function sendDiagnosisEmailReport(diagnosisId: string): Promise<void> {
  // Null-entity graph service action → POST /api/v1/graph/report.
  const info = new ActionInfo('report', null, null, 'POST');
  info.bodyParameters = { diagnosis_id: diagnosisId };
  await dataManager.callAction<{ diagnosis_id: string }, { sent: boolean }>(info);
}

export interface CreateAndSendParams {
  /** Required for project-local conversations; for a cross-user bundle it is
   *  the sender's local-only project mapping — pass it whenever one is selected. */
  project_id?: string | null;
  members: ConversationParticipant[];
  title?: string;
  /** Serialized TypeIds stamped as the new conversation's shared context at
   *  create time (constructor-lift into ``_shared_context_entities_``). */
  shared_context_entities?: string[];
}

export interface CreateAndSendResult {
  conversation_id: string;
}

/**
 * Cross-user routing predicate: a conversation is delivered through the hub
 * bundle path (vs. project-local) when *any* participant carries a user_id
 * or an @-email — i.e. could be a different machine.
 */
export const hasRemoteParticipant = (ps: ConversationParticipant[]): boolean =>
  ps.some((p) => !!p.user_id || (!!p.email && p.email.includes('@')));

/**
 * Create-or-resume a conversation WITHOUT sending anything into it — the
 * create+share half of ``createAndSendConversation``, exposed for commits
 * that put their content in by another means (e.g. ``forwardMessage``).
 * ``draftRef`` (when provided) preserves the same Conversation id across
 * retries so a transient invite failure doesn't orphan a hub conversation.
 */
export async function createConversationForShare(
  params: CreateAndSendParams,
  opts?: {
    ensureCloudLogin?: () => Promise<{ ok: true } | { ok: false; error: string }>;
    draftRef?: { current: Conversation | null };
  },
): Promise<CreateAndSendResult> {
  let conversationId: string;

  if (hasRemoteParticipant(params.members)) {
    if (opts?.ensureCloudLogin) {
      const gate = await opts.ensureCloudLogin();
      if (!gate.ok) throw new Error(gate.error);
    }
    // Two ways to admit someone, because the address book knows people two
    // ways. A contact learned from a conversation roster carries a hub user_id
    // and no email (the hub does not disclose other people's addresses), and
    // ContactPicker deliberately offers those contacts — so requiring an email
    // here made every one of them pickable but unsendable. An email wins when a
    // participant has both: it is the path that also works for someone without
    // an account yet.
    const invitees: string[] = [];
    for (const p of params.members) {
      const email = normalizeEmail(p.email) || '';
      if (email.includes('@')) {
        invitees.push(email);
        continue;
      }
      const userId = (p.user_id || '').trim();
      if (userId) invitees.push(userId);
    }
    if (invitees.length === 0) {
      throw new Error('At least one recipient email or hub contact is required');
    }

    const conv =
      opts?.draftRef?.current ??
      new Conversation({
        title: params.title,
        members: params.members,
        // Local-only project mapping (the hub body strips project_id) — without
        // it a remote-shared conversation loses its project association and the
        // sender's conversation list can't scope it.
        ...(params.project_id ? { project_id: params.project_id } : {}),
        // ``shared_context_entities`` is a wire-lifted field (not on IConversation);
        // the APIEntity base moves it into ``_shared_context_entities_`` on construct.
        ...(params.shared_context_entities && params.shared_context_entities.length > 0
          ? { shared_context_entities: params.shared_context_entities }
          : {}),
      } as Partial<Conversation>);
    if (params.title !== undefined) conv.title = params.title;
    conv.members = params.members;
    if (opts?.draftRef) opts.draftRef.current = conv;

    await conv.save();
    // One hub MembershipRequest per invitee — the hub's own call; the desktop answers it too, publishing
    // the conversation with the first one, so the rest go out together after it.
    const [first, ...rest] = invitees;
    await conv.inviteMember(first);
    await Promise.all(rest.map((invitee) => conv.inviteMember(invitee)));
    conversationId = conv.id;
  } else {
    if (!params.project_id) {
      throw new Error('project_id is required for project-local conversations');
    }
    const r = await createProjectConversation({
      project_id: params.project_id,
      members: params.members,
      title: params.title,
      // Let the backend derive the owning project from the shared entity
      // (deterministic), with ``project_id`` as the ambient fallback.
      shared_context_entities: params.shared_context_entities,
    });
    conversationId = r.conversation_id;
  }

  return { conversation_id: conversationId };
}

/**
 * Create-or-resume a conversation and send the first message.
 * ``draftRef`` (when provided) preserves the same Conversation id across
 * retries so a transient share() failure doesn't orphan a hub conversation.
 */
export async function createAndSendConversation(
  params: CreateAndSendParams,
  payload: ConversationSendPayload,
  opts?: {
    ensureCloudLogin?: () => Promise<{ ok: true } | { ok: false; error: string }>;
    draftRef?: { current: Conversation | null };
  },
): Promise<CreateAndSendResult> {
  const { conversation_id: conversationId } = await createConversationForShare(params, opts);

  // A conversation may start with just a title — send only when there is content.
  const hasContent =
    !!payload.text?.trim() ||
    !!payload.files?.length ||
    !!payload.assetReferences?.length ||
    !!payload.sharedContextEntities?.length ||
    !!payload.shareConfig;
  if (hasContent) await sendToExistingConversation(conversationId, payload);

  if (opts?.draftRef) opts.draftRef.current = null;
  return { conversation_id: conversationId };
}
